import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  TextField,
  Chip,
  Button,
  Popover,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Divider,
  Paper,
  Badge,
  Tooltip,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Store as StoreIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  LocalPizza as PizzaIcon,
  Restaurant as RestaurantIcon,
  Fastfood as FastfoodIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useStores } from '../hooks/useStores';

interface Store {
  id: string;
  name: string;
  brand: string;
  city?: string;
  state?: string;
  region?: string;
  active?: boolean;
}

interface EnhancedStoreSelectorProps {
  selectedStores: string[];
  onStoresChange: (storeIds: string[]) => void;
  maxSelection?: number;
}

const brandIcons: Record<string, React.ReactElement> = {
  "Happy's Pizza": <PizzaIcon />,
  "Savvy Sliders": <FastfoodIcon />,
  "BurgerFi": <RestaurantIcon />,
  "Fat Boys Pizza": <PizzaIcon />,
};

const brandColors: Record<string, string> = {
  "Happy's Pizza": '#FF6B6B',
  "Savvy Sliders": '#4ECDC4',
  "BurgerFi": '#AF2326',
  "Fat Boys Pizza": '#FFD93D',
};

const EnhancedStoreSelector: React.FC<EnhancedStoreSelectorProps> = ({
  selectedStores,
  onStoresChange,
  maxSelection,
}) => {
  const theme = useTheme();
  const { data: storesData, isLoading } = useStores();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [selectedStates, setSelectedStates] = useState<string[]>([]);
  const [expandedBrands, setExpandedBrands] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);

  const stores = useMemo(() => {
    if (!storesData || !Array.isArray(storesData)) return [];
    return storesData.map((store: any) => ({
      id: store.id || store.storeNumber,
      name: store.name || store.storeName,
      brand: store.brand || detectBrand(store.name || store.storeName),
      city: store.city,
      state: store.state,
      region: store.region,
      active: store.active !== false,
    }));
  }, [storesData]);

  // Detect brand from store name
  function detectBrand(storeName: string): string {
    const name = storeName.toLowerCase();
    if (name.includes('happy')) return "Happy's Pizza";
    if (name.includes('savvy')) return 'Savvy Sliders';
    if (name.includes('burger')) return 'BurgerFi';
    if (name.includes('fat boy')) return 'Fat Boys Pizza';
    return 'Other';
  }

  // Group stores by brand
  const storesByBrand = useMemo(() => {
    const grouped: Record<string, Store[]> = {};
    stores.forEach((store: Store) => {
      if (!grouped[store.brand]) grouped[store.brand] = [];
      grouped[store.brand].push(store);
    });
    return grouped;
  }, [stores]);

  // Get unique states
  const uniqueStates = useMemo(() => {
    const states = new Set<string>();
    stores.forEach((store: Store) => {
      if (store.state) states.add(store.state);
    });
    return Array.from(states).sort();
  }, [stores]);

  // Filter stores based on search and filters
  const filteredStores = useMemo(() => {
    let filtered = stores;

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((store: Store) =>
        store.name.toLowerCase().includes(term) ||
        store.city?.toLowerCase().includes(term) ||
        store.state?.toLowerCase().includes(term) ||
        store.id.toString().includes(term)
      );
    }

    // Filter by selected brands
    if (selectedBrands.length > 0) {
      filtered = filtered.filter((store: Store) => selectedBrands.includes(store.brand));
    }

    // Filter by selected states
    if (selectedStates.length > 0) {
      filtered = filtered.filter((store: Store) => store.state && selectedStates.includes(store.state));
    }

    return filtered;
  }, [stores, searchTerm, selectedBrands, selectedStates]);

  // Group filtered stores by brand
  const filteredStoresByBrand = useMemo(() => {
    const grouped: Record<string, Store[]> = {};
    filteredStores.forEach((store: Store) => {
      if (!grouped[store.brand]) grouped[store.brand] = [];
      grouped[store.brand].push(store);
    });
    return grouped;
  }, [filteredStores]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleStoreToggle = (storeId: string) => {
    if (selectedStores.includes(storeId)) {
      onStoresChange(selectedStores.filter(id => id !== storeId));
    } else {
      if (!maxSelection || selectedStores.length < maxSelection) {
        onStoresChange([...selectedStores, storeId]);
      }
    }
  };

  const handleBrandToggle = (brand: string) => {
    const brandStores = storesByBrand[brand] || [];
    const brandStoreIds = brandStores.map(s => s.id);
    const allSelected = brandStoreIds.every(id => selectedStores.includes(id));

    if (allSelected) {
      // Deselect all stores from this brand
      onStoresChange(selectedStores.filter(id => !brandStoreIds.includes(id)));
    } else {
      // Select all stores from this brand
      const combinedArray = [...selectedStores, ...brandStoreIds];
      const newSelection = Array.from(new Set(combinedArray));
      if (!maxSelection || newSelection.length <= maxSelection) {
        onStoresChange(newSelection);
      }
    }
  };

  const handleBrandFilterToggle = (brand: string) => {
    if (selectedBrands.includes(brand)) {
      setSelectedBrands(selectedBrands.filter(b => b !== brand));
    } else {
      setSelectedBrands([...selectedBrands, brand]);
    }
  };

  const handleStateFilterToggle = (state: string) => {
    if (selectedStates.includes(state)) {
      setSelectedStates(selectedStates.filter(s => s !== state));
    } else {
      setSelectedStates([...selectedStates, state]);
    }
  };

  const toggleBrandExpansion = (brand: string) => {
    if (expandedBrands.includes(brand)) {
      setExpandedBrands(expandedBrands.filter(b => b !== brand));
    } else {
      setExpandedBrands([...expandedBrands, brand]);
    }
  };

  const clearSelection = () => {
    onStoresChange([]);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedBrands([]);
    setSelectedStates([]);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Badge badgeContent={selectedStores.length} color="primary">
          <Button
            variant="outlined"
            startIcon={<StoreIcon />}
            endIcon={<ExpandMoreIcon />}
            onClick={handleOpen}
            sx={{
              borderColor: alpha(theme.palette.primary.main, 0.3),
              '&:hover': {
                borderColor: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              },
            }}
          >
            {selectedStores.length === 0
              ? 'Select Stores'
              : selectedStores.length === 1
              ? stores.find((s: Store) => s.id === selectedStores[0])?.name || 'Store Selected'
              : `${selectedStores.length} Stores Selected`}
          </Button>
        </Badge>
        
        {selectedStores.length > 0 && (
          <Tooltip title="Clear selection">
            <IconButton size="small" onClick={clearSelection}>
              <ClearIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            width: 600,
            maxHeight: 600,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" gutterBottom>
            Select Stores
          </Typography>
          
          {/* Search Bar */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name, city, state, or store ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {/* Quick Actions */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              size="small"
              startIcon={<FilterListIcon />}
              onClick={() => setShowFilters(!showFilters)}
              variant={showFilters ? 'contained' : 'outlined'}
            >
              Filters {selectedBrands.length + selectedStates.length > 0 && 
                `(${selectedBrands.length + selectedStates.length})`}
            </Button>
            
            {Object.keys(storesByBrand).map(brand => (
              <Chip
                key={brand}
                label={`${brand} (${storesByBrand[brand].length})`}
                icon={brandIcons[brand]}
                onClick={() => handleBrandFilterToggle(brand)}
                color={selectedBrands.includes(brand) ? 'primary' : 'default'}
                variant={selectedBrands.includes(brand) ? 'filled' : 'outlined'}
                sx={{
                  borderColor: brandColors[brand],
                  color: selectedBrands.includes(brand) ? 'white' : brandColors[brand],
                  backgroundColor: selectedBrands.includes(brand) ? brandColors[brand] : 'transparent',
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Filters Panel */}
        <Collapse in={showFilters}>
          <Paper sx={{ p: 2, m: 2, mt: 0, backgroundColor: alpha(theme.palette.primary.main, 0.02) }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Filter by State</Typography>
              {(selectedBrands.length > 0 || selectedStates.length > 0) && (
                <Button size="small" onClick={clearFilters}>
                  Clear Filters
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {uniqueStates.map(state => (
                <Chip
                  key={state}
                  label={state}
                  size="small"
                  onClick={() => handleStateFilterToggle(state)}
                  color={selectedStates.includes(state) ? 'primary' : 'default'}
                  variant={selectedStates.includes(state) ? 'filled' : 'outlined'}
                />
              ))}
            </Box>
          </Paper>
        </Collapse>

        {/* Store List */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          {Object.entries(filteredStoresByBrand).map(([brand, brandStores]) => {
            const brandStoreIds = brandStores.map(s => s.id);
            const selectedCount = brandStoreIds.filter(id => selectedStores.includes(id)).length;
            const isExpanded = expandedBrands.includes(brand) || selectedCount > 0 || Object.keys(filteredStoresByBrand).length === 1;

            return (
              <Box key={brand} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 1,
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.8 },
                  }}
                  onClick={() => toggleBrandExpansion(brand)}
                >
                  <IconButton size="small">
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                  {brandIcons[brand]}
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1 }}>
                    {brand}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedCount > 0 && `${selectedCount}/`}{brandStores.length} stores
                  </Typography>
                  <Checkbox
                    checked={selectedCount === brandStores.length && selectedCount > 0}
                    indeterminate={selectedCount > 0 && selectedCount < brandStores.length}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBrandToggle(brand);
                    }}
                  />
                </Box>

                <Collapse in={isExpanded}>
                  <List dense sx={{ pl: 4 }}>
                    {brandStores.map(store => (
                      <ListItemButton
                        key={store.id}
                        onClick={() => handleStoreToggle(store.id)}
                        selected={selectedStores.includes(store.id)}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          '&.Mui-selected': {
                            backgroundColor: alpha(brandColors[brand], 0.1),
                          },
                        }}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={selectedStores.includes(store.id)}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={store.name}
                          secondary={
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationIcon sx={{ fontSize: 12 }} />
                              <Typography component="span" variant="caption">
                                {store.city && `${store.city}, `}{store.state} • ID: {store.id}
                              </Typography>
                            </Box>
                          }
                        />
                        {!store.active && (
                          <Chip label="Inactive" size="small" color="warning" />
                        )}
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              </Box>
            );
          })}

          {filteredStores.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                No stores found matching your criteria
              </Typography>
              {(searchTerm || selectedBrands.length > 0 || selectedStates.length > 0) && (
                <Button size="small" onClick={clearFilters} sx={{ mt: 1 }}>
                  Clear Filters
                </Button>
              )}
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {selectedStores.length} of {stores.length} stores selected
          </Typography>
          <Button variant="contained" onClick={handleClose}>
            Done
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default EnhancedStoreSelector;